import React, { useState } from 'react';
import { Card, Col, Row, Statistic, Progress, Typography, Button, Layout, Menu } from 'antd';
import { createStyles } from 'antd-style';
import { 
  BarChartOutlined, 
  CommentOutlined, 
  UserOutlined, 
  ClockCircleOutlined,
  RiseOutlined,
  HomeOutlined,
  DashboardOutlined,
  EnvironmentOutlined,
  MessageOutlined,
  TeamOutlined,
  LineChartOutlined,
  PieChartOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;
const { Sider, Content } = Layout;

const useStyles = createStyles(({ token }) => {
  return {
    container: {
      minHeight: '100vh',
    },
    header: {
      marginBottom: '24px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    headerTitle: {
      margin: 0,
    },
    card: {
      marginBottom: '24px',
      boxShadow: token.boxShadowTertiary,
    },
    mapChart: {
      height: '400px',
      marginTop: '24px',
    },
    backButton: {
      marginRight: '8px',
    },
    sider: {
      background: token.colorBgContainer,
      boxShadow: token.boxShadowTertiary,
    },
    siderLogo: {
      height: '64px',
      padding: '16px',
      color: token.colorPrimary,
      fontSize: '18px',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      borderBottom: `1px solid ${token.colorBorderSecondary}`,
    },
    content: {
      padding: '24px',
      background: token.colorBgLayout,
      minHeight: '100vh',
    }
  };
});

// 模拟组件
const WordCloud = ({ data }: { data: { word: string; weight: number }[] }) => (
  <div style={{ height: '300px', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
    {data.map((item, index) => (
      <span 
        key={index} 
        style={{ 
          fontSize: `${Math.min(24, Math.max(12, item.weight / 2))}px`,
          padding: '5px 10px',
          margin: '5px',
          color: `hsl(${Math.random() * 360}, 70%, 50%)`,
          fontWeight: item.weight > 20 ? 'bold' : 'normal',
        }}
      >
        {item.word}
      </span>
    ))}
  </div>
);

const ActiveUsersChart = () => (
  <div style={{ height: '300px', display: 'flex', flexDirection: 'column', justifyContent: 'space-around' }}>
    <div style={{ height: '30px', background: 'linear-gradient(90deg, #e6f7ff 0%, #1890ff 100%)', borderRadius: '4px' }}></div>
    <div style={{ height: '30px', background: 'linear-gradient(90deg, #e6f7ff 0%, #1890ff 85%)', borderRadius: '4px' }}></div>
    <div style={{ height: '30px', background: 'linear-gradient(90deg, #e6f7ff 0%, #1890ff 92%)', borderRadius: '4px' }}></div>
    <div style={{ height: '30px', background: 'linear-gradient(90deg, #e6f7ff 0%, #1890ff 78%)', borderRadius: '4px' }}></div>
    <div style={{ height: '30px', background: 'linear-gradient(90deg, #e6f7ff 0%, #1890ff 65%)', borderRadius: '4px' }}></div>
    <div style={{ height: '30px', background: 'linear-gradient(90deg, #e6f7ff 0%, #1890ff 70%)', borderRadius: '4px' }}></div>
    <div style={{ height: '30px', background: 'linear-gradient(90deg, #e6f7ff 0%, #1890ff 88%)', borderRadius: '4px' }}></div>
  </div>
);

const ConversationMap = () => (
  <div style={{ 
    height: '400px', 
    background: 'url(https://gw.alipayobjects.com/zos/rmsportal/HBWnDEUXCnGnGrRfrpKa.png) center center / contain no-repeat' 
  }}></div>
);

// 模拟词云数据
const wordCloudData = [
  { word: '皮肤干燥', weight: 35 },
  { word: '睡眠不好', weight: 30 },
  { word: '肠胃不适', weight: 25 },
  { word: '心情烦躁', weight: 20 },
  { word: '头痛', weight: 18 },
  { word: '养生', weight: 16 },
  { word: '冬季养生', weight: 15 },
  { word: '保养皮肤', weight: 14 },
  { word: '春季过敏', weight: 13 },
  { word: '疲劳', weight: 12 },
  { word: '中医理论', weight: 11 },
  { word: '健康饮食', weight: 10 },
  { word: '免疫力', weight: 9 },
  { word: '运动建议', weight: 8 },
  { word: '中药调理', weight: 7 },
];

const Dashboard: React.FC = () => {
  const { styles } = useStyles();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <Layout className={styles.container}>
      <Sider 
        className={styles.sider} 
        collapsible 
        collapsed={collapsed} 
        onCollapse={(value) => setCollapsed(value)}
        width={220}
      >
        <div className={styles.siderLogo}>
          <DashboardOutlined style={{ marginRight: '8px' }} />
          {!collapsed && <span>炣森科技HolyAI管理</span>}
        </div>
        <Menu
          mode="inline"
          defaultSelectedKeys={['1']}
          items={[
            {
              key: '1',
              icon: <DashboardOutlined />,
              label: '总览',
            },
            {
              key: '2',
              icon: <UserOutlined />,
              label: '用户统计',
              children: [
                {
                  key: '2-1',
                  label: '用户增长',
                  icon: <LineChartOutlined />,
                },
                {
                  key: '2-2',
                  label: '用户活跃度',
                  icon: <BarChartOutlined />,
                },
                {
                  key: '2-3',
                  label: '用户画像',
                  icon: <PieChartOutlined />,
                },
              ],
            },
            {
              key: '3',
              icon: <EnvironmentOutlined />,
              label: '地区统计',
              children: [
                {
                  key: '3-1',
                  label: '地区分布',
                },
                {
                  key: '3-2',
                  label: '地区活跃度',
                },
              ],
            },
            {
              key: '4',
              icon: <CommentOutlined />,
              label: '对话统计',
              children: [
                {
                  key: '4-1',
                  label: '对话数量',
                  icon: <MessageOutlined />,
                },
                {
                  key: '4-2',
                  label: '问题类型',
                  icon: <BarChartOutlined />,
                },
                {
                  key: '4-3',
                  label: '满意度分析',
                  icon: <LineChartOutlined />,
                },
              ],
            },
            {
              key: '5',
              icon: <TeamOutlined />,
              label: '中医知识库',
              children: [
                {
                  key: '5-1',
                  label: '热门知识',
                },
                {
                  key: '5-2',
                  label: '知识更新',
                },
              ],
            },
          ]}
        />
      </Sider>
      <Content className={styles.content}>
        <div className={styles.header}>
          <Title level={2} className={styles.headerTitle}>炣森科技HolyAI - 运营数据看板</Title>
          <Button 
            type="primary" 
            icon={<HomeOutlined />} 
            onClick={() => navigate('/')}
            className={styles.backButton}
          >
            返回主页
          </Button>
        </div>

        <Row gutter={24}>
          <Col xl={6} lg={12} md={12} sm={24} xs={24}>
            <Card className={styles.card}>
              <Statistic 
                title="总用户数" 
                value={12543} 
                prefix={<UserOutlined />} 
                suffix={<RiseOutlined style={{ color: '#3f8600' }} />}
              />
              <div style={{ marginTop: '8px', fontSize: '14px', color: '#3f8600' }}>
                较昨日 +126
              </div>
            </Card>
          </Col>

          <Col xl={6} lg={12} md={12} sm={24} xs={24}>
            <Card className={styles.card}>
              <Statistic 
                title="今日对话数" 
                value={4328} 
                prefix={<CommentOutlined />}
                suffix={<RiseOutlined style={{ color: '#3f8600' }} />}
              />
              <div style={{ marginTop: '8px', fontSize: '14px', color: '#3f8600' }}>
                较昨日 +215
              </div>
            </Card>
          </Col>

          <Col xl={6} lg={12} md={12} sm={24} xs={24}>
            <Card className={styles.card}>
              <Statistic 
                title="平均对话时长" 
                value="4.6分钟" 
                prefix={<ClockCircleOutlined />}
              />
              <div style={{ marginTop: '8px', fontSize: '14px', color: '#faad14' }}>
                较昨日 -0.3分钟
              </div>
            </Card>
          </Col>

          <Col xl={6} lg={12} md={12} sm={24} xs={24}>
            <Card className={styles.card}>
              <Statistic 
                title="满意度" 
                value={95.8} 
                suffix="%" 
                prefix={<BarChartOutlined />}
              />
              <div style={{ marginTop: '8px', fontSize: '14px', color: '#3f8600' }}>
                较昨日 +1.2%
              </div>
            </Card>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xl={18} lg={24} md={24} sm={24} xs={24}>
            <Card title="全国用户分布情况" bordered={false} className={styles.card}>
              <div className={styles.mapChart}>
                <ConversationMap />
              </div>
            </Card>
          </Col>

          <Col xl={6} lg={24} md={24} sm={24} xs={24}>
            <Card
              title="用户活跃度"
              className={styles.card}
              bordered={false}
            >
              <ActiveUsersChart />
            </Card>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xl={12} lg={24} sm={24} xs={24}>
            <Card title="用户群体分布" bordered={false} className={styles.card}>
              <Row style={{ padding: '16px 0' }}>
                <Col span={8}>
                  <Statistic title="男性用户" value="46%" />
                  <Progress type="dashboard" percent={46} />
                </Col>
                <Col span={8}>
                  <Statistic title="女性用户" value="54%" />
                  <Progress type="dashboard" percent={54} />
                </Col>
                <Col span={8}>
                  <Statistic title="新用户比例" value="32%" />
                  <Progress type="dashboard" percent={32} />
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xl={12} lg={24} sm={24} xs={24}>
            <Card
              title="热门问题词云"
              bordered={false}
              className={styles.card}
            >
              <WordCloud data={wordCloudData} />
            </Card>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default Dashboard; 